/**
 * @file PortalPageMultiSelect.js
 * @description A multi-select dropdown component for selecting portal pages.
 */

import React, { useState, useEffect } from 'react';
import { SearchableDropdown } from '../../helpers/SearchableDropdown_style_b_v2';
import { queryScopedPortalPages } from '../../../services/portalPageService';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

/**
 * Represents a multi-select component for portal pages.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {Array} props.selectedPortalPages - The selected portal pages.
 * @param {Function} props.onPortalPagesChange - The callback function to handle portal page selection change.
 * @param {string} [props.label="Portal Pages"] - The label for the component.
 * @param {boolean} [props.isRequired=false] - Indicates if the component is required.
 * @param {*} props.resetTrigger - The trigger to reset the component.
 * @param {*} props.tenantId - The ID of the tenant.
 * @param {boolean} [props.showSelectedItemsInField=true] - Indicates if the selected items should be shown in the field.
 * @param {boolean} [props.showSelectionBox=true] - Indicates if the selection box should be shown.
 * @returns {JSX.Element} The rendered PortalPageMultiSelect component.
 */
const PortalPageMultiSelect = ({
    className,
    selectedPortalPages,
    onPortalPagesChange,
    label = 'Portal Pages',
    isRequired = false,
    resetTrigger,
    tenantId,
    showSelectedItemsInField = true,
    showSelectionBox = true,
    showSelectedCount = false,
}) => {
    const [portalPages, setPortalPages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadPortalPages = async () => {
            try {
                setIsLoading(true);
                const data = await queryScopedPortalPages();
                const cleanedData = data.viewModels.map(({ id, name }) => ({
                    id,
                    name,
                }));
                setPortalPages(cleanedData);
            } catch (error) {
                console.error('Could not fetch portal pages:', error);
                Toast.error(getText('msg_708bab4639a7'));
            } finally {
                setIsLoading(false);
            }
        };

        loadPortalPages();
    }, [tenantId]);

    const handlePortalPagesChange = selectedOptions => {
        onPortalPagesChange(selectedOptions);
    };

    return (
        <div className={`form-group mb-3 ${className}`}>
            <label htmlFor="portal-page-dropdown">
                {label}
                {isRequired && <span className="text-danger">*</span>}
            </label>
            <SearchableDropdown
                id="portal-page-dropdown"
                options={portalPages}
                optionIDField="id"
                updateOptions={handlePortalPagesChange}
                savedOptions={selectedPortalPages}
                savedOptionIDField="id"
                disabled={portalPages.length === 0}
                isLoading={isLoading}
                resetTrigger={resetTrigger}
                showSelectedItemsInField={showSelectedItemsInField}
                showSelectionBox={showSelectionBox}
                showSelectedCount={showSelectedCount}
                allSelectedLabel={getText('msg_bea13bcdc919')}
            />
        </div>
    );
};

export default PortalPageMultiSelect;
